/**
 * PATH: packages/ui/src/kanban/components/card-detail/MembersSection.tsx
 * FILE_ID: UI-KANBAN-CMP-112
 * PURPOSE: Assignee picker + avatar list section.
 * WHY: Split from the 4,176-line CardDetailDrawer monolith (UAT P5).
 *      Verbatim move - behaviour unchanged; the host re-exports the
 *      public surface so existing imports keep working.
 */

import { type ReactNode, useMemo, useRef, useState } from 'react'
import {
  Archive, Calendar, CheckSquare, ChevronUp, Clock, CornerDownRight, ExternalLink, FileText, Flag,
  Loader2, Mail, Merge, MessageSquare, Network, Paperclip, Pin, PinOff, Plus,
  RotateCcw,
  ShieldAlert, Tag as TagIcon, Trash2, UserPlus, X,
} from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../api'
import { KanbanCardConflictError } from '../../api'
import { MentionTextarea } from '../MentionTextarea'
import { PeoplePicker, kanbanMemberToPerson } from '../PeoplePicker'
import type {
  AuditLogEntry,
  Card, CardChecklistItemT, CardChecklistT, CardCommentT,
  CardStatus, Lane, Tag,
} from '../../types'
import { KanbanAvatar } from '../KanbanAvatar'
import { EmailLinkTickCross } from '../EmailLinkTickCross'
import { ChecklistItemContextMenu } from '../ChecklistItemContextMenu'
import { SpeechMicButton } from '../SpeechMicButton'
import type { TrelloCardDetail } from '../../types'
import { useRegisterUndo } from '../../undo/UndoContext'


// =============================================================================
// MembersSection — assignee picker + avatar list
// =============================================================================

export function MembersSection({
  card,
  members,
  api,
  onChanged,
  hideTitle = false,
}: {
  card: Card
  members: KanbanMember[]
  api: KanbanApi
  onChanged?: (card: Card) => void
  /** ARC-4: suppress the inner h3 when a CollapsibleSection hosts this. */
  hideTitle?: boolean
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // ARC-8 MR2 — assignee toggles register on the board's universal Ctrl+Z
  // stack (null when the drawer is opened outside a board surface).
  const registerUndo = useRegisterUndo()
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged

  const assigned = members.filter((m) => card.assigned_user_ids.includes(m.id))
  const memberById = useMemo(() => {
    const map = new Map<number, KanbanMember>()
    for (const m of members) map.set(m.id, m)
    return map
  }, [members])

  const toggle = async (member: KanbanMember, isAssigned: boolean) => {
    setBusy(member.id)
    setError(null)
    try {
      const updated = isAssigned
        ? await api.detachAssignee(card.id, member.id)
        : await api.attachAssignee(card.id, member.id)
      onChanged?.(updated)
      // Register AFTER server success; compensating call routes through
      // the same audit-evented attach/detachAssignee endpoints.
      registerUndo?.({
        label: isAssigned ? `Unassign ${member.name}` : `Assign ${member.name}`,
        undo: async () => {
          const c = isAssigned
            ? await api.attachAssignee(card.id, member.id)
            : await api.detachAssignee(card.id, member.id)
          onChangedRef.current?.(c)
        },
        redo: async () => {
          const c = isAssigned
            ? await api.detachAssignee(card.id, member.id)
            : await api.attachAssignee(card.id, member.id)
          onChangedRef.current?.(c)
        },
      })
    } catch (e) {
      // BUG-2026-06-29-KANBAN-MEMBER-EDIT-SILENT-FAIL — a failed assign /
      // unassign used to stop the spinner with no feedback, so the operator
      // assumed it worked. Surface it inline.
      setError(
        e instanceof Error ? e.message : 'Could not update the assignee. Please try again.',
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <section>
      <div className={`mb-1 flex items-center ${hideTitle ? 'justify-end' : 'justify-between'}`}>
        {/* ARC-4: hideTitle when hosted inside a CollapsibleSection (which
            already renders the "Members" header + count). */}
        {!hideTitle && (
          <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
            Members
          </h3>
        )}
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-800 px-1.5 py-0.5 text-[11px] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-900"
        >
          <UserPlus className="h-3 w-3" /> Assign
        </button>
      </div>
      {assigned.length === 0 ? (
        <p className="text-xs text-neutral-400">Nobody assigned yet.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {assigned.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => void toggle(m, true)}
              disabled={busy === m.id}
              title={`${m.name} — click to unassign`}
              className="group inline-flex items-center gap-1.5 rounded-full border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 pl-0.5 pr-2 py-0.5 text-[11px] text-neutral-700 dark:text-neutral-200 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-700 dark:hover:text-red-300"
            >
              <KanbanAvatar member={m} size={18} />
              <span className="truncate max-w-[10rem]">{m.name}</span>
              {busy === m.id ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <X className="h-2.5 w-2.5 opacity-40 transition-opacity group-hover:opacity-100" />
              )}
            </button>
          ))}
        </div>
      )}
      {pickerOpen && (
        <PeoplePicker
          people={members.map(kanbanMemberToPerson)}
          selectedIds={card.assigned_user_ids}
          onToggle={(id) => {
            const m = memberById.get(id)
            if (m) void toggle(m, card.assigned_user_ids.includes(id))
          }}
          busyId={busy}
          hint="Click to assign / unassign. Stays open for multiple."
          maxHeightClassName="max-h-44"
          className="mt-2 overflow-hidden rounded-md border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950"
        />
      )}
      {error && (
        <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">{error}</p>
      )}
    </section>
  )
}
